import { computed, inject } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { firstValueFrom } from 'rxjs';
import {
	DEPOSIT_WALLET_KEY,
	emptyWalletBalance,
	getWalletBuyingPower,
	KALA_WALLET_KEYS,
	KalaWalletKey,
	WalletBalance
} from '../../../models/wallet.model';
import { CommodityAccessService } from '../../../services/commodity-access.service';
import { WalletService } from '../../../services/wallet.service';

export interface WalletStoreState {
	/** Every wallet this build knows, always present — an unread one simply reads 0. */
	wallets: Record<KalaWalletKey, WalletBalance>;
	/**
	 * Whether a read has ever landed for each wallet.
	 *
	 * Kept because {@link WalletStoreState.wallets} cannot answer it: a wallet that
	 * was never read and a wallet that is genuinely empty both hold 0, and a screen
	 * that prints the first as «۰ ریال» is claiming an empty wallet it has no
	 * grounds for. Consumers ask this before printing a figure at all.
	 */
	loaded: Record<KalaWalletKey, boolean>;
	loading: boolean;
	error: string | null;
}

const initialState: WalletStoreState = {
	wallets: Object.fromEntries(KALA_WALLET_KEYS.map(key => [key, emptyWalletBalance(key)])) as Record<
		KalaWalletKey,
		WalletBalance
	>,
	loaded: Object.fromEntries(KALA_WALLET_KEYS.map(key => [key, false])) as Record<KalaWalletKey, boolean>,
	loading: false,
	error: null
};

/**
 * The customer's wallet money, held above the screens that print it.
 *
 * Root-provided because three unrelated places read the same figures and none of
 * them owns the others: the کیف پول card, the قدرت خرید strip pinned to the bottom
 * of every main screen, and the order form's summary. One store means the order
 * form and the strip can never disagree about what is left to trade with.
 *
 * The summary endpoint returns every wallet at once. A null block falls back to
 * the difference between balance and available balance when the latter is present.
 */
export const WalletStore = signalStore(
	{ providedIn: 'root' },
	withState(initialState),
	withComputed(({ wallets, loaded }) => ({
		/** کیف گواهی سپرده — the wallet the bottom bar and the order form both answer to. */
		depositWallet: computed(() => wallets()[DEPOSIT_WALLET_KEY]),
		/**
		 * قدرت خرید گواهی سپرده. Fixed to that wallet whatever the trader chose as
		 * their کیف پیش‌فرض, per the handoff.
		 */
		depositBuyingPower: computed(() => getWalletBuyingPower(wallets()[DEPOSIT_WALLET_KEY])),
		/** Whether the گواهی سپرده figures on screen are a read answer rather than the empty opening one. */
		depositLoaded: computed(() => loaded()[DEPOSIT_WALLET_KEY]),
		/** مجموع مانده همه کیف‌های کاربر برای گزارش دارایی. */
		totalBalance: computed(() => KALA_WALLET_KEYS.reduce((total, key) => total + wallets()[key].balance, 0))
	})),
	withMethods((store, walletService = inject(WalletService), commodityAccess = inject(CommodityAccessService)) => {
		/** Answers to a superseded request are dropped rather than written late. */
		let generation = 0;

		return {
			/**
			 * Refresh all wallet balances from the summary endpoint.
			 *
			 * Never clears what is on screen: a failed refresh leaves the last figures
			 * standing beside the error, because a balance one request old is a better
			 * answer than a wallet that suddenly reads 0.
			 *
			 * @returns `true` when the summary was refreshed, `false` when the read failed.
			 */
			async load(): Promise<boolean> {
				const requestGeneration = ++generation;
				patchState(store, { loading: true, error: null });

				try {
					const summary = await firstValueFrom(walletService.getWalletSummary());
					if (requestGeneration !== generation) {
						return false;
					}

					const wallets = { ...store.wallets() };
					const loaded = { ...store.loaded() };
					for (const item of summary) {
						if (item.application === 'None') {
							continue;
						}

						const blocked =
							item.block ??
							(item.availableBalance === null ? 0 : Math.max(0, item.balance - item.availableBalance));
						wallets[item.application] = { key: item.application, balance: item.balance, blocked };
						loaded[item.application] = true;
					}

					patchState(store, {
						wallets,
						loaded,
						loading: false
					});

					return true;
				} catch {
					if (requestGeneration !== generation) {
						return false;
					}

					// A customer without a کد بورس کالا is refused this read by the endpoint itself,
					// and an account with no commodity money on it is what the refusal means — so the
					// card stands on its opening zeros rather than the screen reporting a failure.
					// `readFailed` reads the null error and leaves the card up.
					patchState(
						store,
						commodityAccess.denied()
							? { error: null, loading: false }
							: { error: 'دریافت مانده کیف پول با خطا مواجه شد', loading: false }
					);

					return false;
				}
			},

			/** One wallet's money as it currently stands. */
			walletOf(key: KalaWalletKey): WalletBalance {
				return store.wallets()[key];
			}
		};
	})
);
